import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
	Button,
	Space,
	Typography,
	Alert,
	Select,
	Divider,
	Empty,
	Tag,
	Tooltip,
} from 'antd';
import { ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import BallooningViewer from '../../components/BallooningViewer';
import BallooningPages from '../../components/BallooningPages';
import {
	useGetBallooningProjectQuery,
	useLazySearchReferenceProjectsQuery,
} from '../../redux/api/ballooning';
import {
	Balloon,
	BallooningPage,
	PreviousBallooningProject,
	ReferenceBallooningProject,
} from '../../interfaces/Ballooning';

const { Option, OptGroup } = Select;

interface BallooningReusabilityCheckerProps {
	orderLineItem: string | null;
	isUserRequest: boolean;
	onClose: () => void;
	onApprove: (balloons: Balloon[], reusedFromId: string | null) => void;
}

// Mirrors REFERENCE_QUERY_MIN_LENGTH on the backend, which rejects shorter queries.
const SEARCH_MIN_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 350;

const formatSourceLabel = (
	project: Pick<PreviousBallooningProject, 'order_name' | 'order_line_item_name'>
) => {
	const orderNumber = project.order_name?.trim() || 'Unknown order';
	const lineItem = project.order_line_item_name?.trim();
	return lineItem ? `${orderNumber} — ${lineItem}` : orderNumber;
};

const truncate = (text: string | undefined | null, max = 60) =>
	text && text.length > max ? `${text.slice(0, max)}…` : text || '';

/** Reproject one page's balloons from the source drawing onto the target drawing. */
const scaleBalloonsOntoPage = (
	sourcePage: BallooningPage,
	targetPage: BallooningPage
): Balloon[] => {
	const [oldWidth, oldHeight] = sourcePage.get_image_dimensions || [1, 1];
	const [newWidth, newHeight] = targetPage.get_image_dimensions || [1, 1];

	return (sourcePage.balloons || []).map((balloon) => ({
		...balloon,
		coordinate_x: balloon.coordinate_x * (newWidth / oldWidth),
		coordinate_y: balloon.coordinate_y * (newHeight / oldHeight),
	}));
};

const BallooningReusabilityChecker: React.FC<
	BallooningReusabilityCheckerProps
> = ({ isUserRequest, onClose, onApprove }) => {
	const { id: projectId } = useParams<{ id: string }>();
	const { data: projectData, isFetching: projectLoading } =
		useGetBallooningProjectQuery({ id: projectId! });

	const [pageNumber, setPageNumber] = useState<number>(1);
	const [pageID, setPageID] = useState<string | null>(null);
	// scaledBalloons: source balloons reprojected onto the CURRENT drawing (left viewer)
	const [scaledBalloons, setScaledBalloons] = useState<Balloon[]>([]);
	// sourcePageBalloons: source balloons in their ORIGINAL coordinates (right viewer)
	const [sourcePageBalloons, setSourcePageBalloons] = useState<Balloon[]>([]);
	const [activeBalloonId, setActiveBalloonId] = useState<string | null>(null);
	const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
		null
	);
	const [searchResults, setSearchResults] = useState<
		ReferenceBallooningProject[]
	>([]);

	const [runSearch, { isFetching: searchLoading }] =
		useLazySearchReferenceProjectsQuery();

	const previousProjects: PreviousBallooningProject[] = useMemo(
		() => projectData?.previous_ballooning_projects ?? [],
		[projectData?.previous_ballooning_projects]
	);

	// Backend already returns them ordered by -created_time, but resort defensively
	// so the "Most recent" badge always reflects the truth, even if the API contract changes.
	const previousProjectsSorted = useMemo(
		() =>
			[...previousProjects].sort(
				(a, b) =>
					new Date(b.created_time).getTime() -
					new Date(a.created_time).getTime()
			),
		[previousProjects]
	);

	const verifiedIds = useMemo(
		() => new Set(previousProjectsSorted.map((p) => p.id)),
		[previousProjectsSorted]
	);

	// Search can return a project that partsbadger_api already matched; keep it in
	// the verified group only, so its provenance is never shown ambiguously.
	const searchOnlyResults = useMemo(
		() => searchResults.filter((r) => !verifiedIds.has(r.id)),
		[searchResults, verifiedIds]
	);

	// "Most recent" badge always anchors to the actual most-recent option so the
	// user can confirm at a glance that the auto-selected default IS the latest
	// (instead of the badge silently hopping to the next-best option whenever the
	// most-recent is the active selection).
	const mostRecentProjectId = useMemo(
		() => previousProjectsSorted[0]?.id ?? null,
		[previousProjectsSorted]
	);

	// Default-select the most recent previous project on mount for BOTH flows
	// (auto-open AND user-triggered). Never start blank when we have a candidate.
	useEffect(() => {
		if (!selectedProjectId && previousProjectsSorted.length > 0) {
			setSelectedProjectId(previousProjectsSorted[0].id);
		}
	}, [previousProjectsSorted, selectedProjectId]);

	// The picker's search rows are deliberately light (no pages, no balloons), so the
	// selected source is always retrieved in full. Verified candidates arrive complete
	// on the project payload, but going through the same fetch keeps one code path.
	const { data: sourceProject, isFetching: sourceLoading } =
		useGetBallooningProjectQuery(
			{ id: selectedProjectId! },
			{ skip: !selectedProjectId }
		);

	const isUnverifiedSelection = Boolean(
		selectedProjectId && !verifiedIds.has(selectedProjectId)
	);

	const selectedLabel = useMemo(() => {
		const verified = previousProjectsSorted.find(
			(p) => p.id === selectedProjectId
		);
		if (verified) return formatSourceLabel(verified);
		const found = searchResults.find((r) => r.id === selectedProjectId);
		return found ? formatSourceLabel(found) : '';
	}, [previousProjectsSorted, searchResults, selectedProjectId]);

	useEffect(() => {
		if (projectData?.ballooning_pages) {
			const selectedPage: BallooningPage | undefined =
				projectData.ballooning_pages.find(
					(p: BallooningPage) => p.page_number === pageNumber
				);
			if (selectedPage) {
				setPageID(selectedPage.id);
			}
		}
	}, [projectData, pageNumber]);

	const handleSearch = useCallback(
		(value: string) => {
			const query = value.trim();
			if (query.length < SEARCH_MIN_LENGTH) {
				setSearchResults([]);
				return;
			}
			runSearch({ q: query, exclude: projectId })
				.unwrap()
				.then((data) => setSearchResults(data.results))
				// A failed lookup just means no extra options; the verified group stands.
				.catch(() => setSearchResults([]));
		},
		[projectId, runSearch]
	);

	const [searchTerm, setSearchTerm] = useState('');

	useEffect(() => {
		const timer = setTimeout(() => handleSearch(searchTerm), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [searchTerm, handleSearch]);

	// Recompute the scaled + raw balloons whenever the selected source, the current
	// project, or the page changes. This powers the "reload" behavior the user asked
	// for — switching between sources re-renders both previews without a manual reset.
	useEffect(() => {
		if (!selectedProjectId || !projectData?.ballooning_pages) {
			setScaledBalloons([]);
			setSourcePageBalloons([]);
			return;
		}

		const sourcePages: BallooningPage[] = sourceProject?.ballooning_pages ?? [];
		const sourcePage = sourcePages.find(
			(p: BallooningPage) => p.page_number === pageNumber
		);
		const targetPage = projectData.ballooning_pages.find(
			(p: BallooningPage) => p.page_number === pageNumber
		);

		if (!sourcePage || !targetPage) {
			setScaledBalloons([]);
			setSourcePageBalloons(sourcePage?.balloons || []);
			return;
		}

		setScaledBalloons(scaleBalloonsOntoPage(sourcePage, targetPage));
		setSourcePageBalloons(sourcePage.balloons || []);
	}, [pageNumber, selectedProjectId, projectData, sourceProject]);

	// Balloons for EVERY page of the source, not just the page being previewed —
	// each page scaled by its own dimensions and mapped to the target page that
	// shares its page number. Source pages with no counterpart are reported rather
	// than silently dropped.
	const importableBalloons = useMemo(() => {
		const targetPages: BallooningPage[] = projectData?.ballooning_pages ?? [];
		const sourcePages: BallooningPage[] = sourceProject?.ballooning_pages ?? [];
		const targetByNumber = new Map<number, BallooningPage>(
			targetPages.map((page) => [page.page_number, page])
		);

		const balloons: Balloon[] = [];
		let droppedPages = 0;

		sourcePages.forEach((sourcePage) => {
			if (!(sourcePage.balloons || []).length) return;
			const targetPage = targetByNumber.get(sourcePage.page_number);
			if (!targetPage) {
				droppedPages += 1;
				return;
			}
			scaleBalloonsOntoPage(sourcePage, targetPage).forEach((balloon) => {
				balloons.push({
					...balloon,
					id: `temp-${balloon.id}`,
					ballooning_page: targetPage.id,
					coordinate_x: Math.round(balloon.coordinate_x),
					coordinate_y: Math.round(balloon.coordinate_y),
				});
			});
		});

		return { balloons, droppedPages };
	}, [projectData?.ballooning_pages, sourceProject?.ballooning_pages]);

	const handleApprove = () => {
		if (!selectedProjectId || !projectData) return;
		if (importableBalloons.balloons.length === 0) return;

		onApprove(importableBalloons.balloons, selectedProjectId);
		onClose();
	};

	const handleCancel = () => {
		onClose();
	};

	const formatCurrentTitle = () => {
		const orderName = projectData?.order_line_item?.order?.name?.trim();
		const itemName = projectData?.order_line_item?.name?.trim();
		const combined = [orderName, itemName].filter(Boolean).join(' — ');
		return truncate(combined, 60);
	};

	const hasPreviousProjects = previousProjectsSorted.length > 0;
	const currentPartName = projectData?.order_line_item?.name?.trim();

	return (
		<div className="flex flex-col w-full h-full min-h-0 overflow-hidden">
			<div className="p-4 bg-white border-b border-gray-200 flex flex-col gap-2">
				{!isUserRequest && hasPreviousProjects && (
					<Alert
						message="Reusable Ballooning Projects Found"
						description="We’ve found existing ballooning projects that can be reused for this reorder. Pick one from the dropdown below to preview and approve."
						type="info"
						showIcon
					/>
				)}
				{!hasPreviousProjects && !projectLoading && (
					<Alert
						message="No matched previous order for this part"
						description="Nothing was ordered before that this part is matched to, so there is no automatic candidate. Search by part or order name to reuse a comparable part instead."
						type="info"
						showIcon
					/>
				)}
				{isUnverifiedSelection && (
					<Alert
						message={`Reusing a part that is not matched to this one${
							selectedLabel ? `: ${selectedLabel}` : ''
						}`}
						description="Its nominals and tolerances come from a different print, so check every balloon against this part's drawing. Marking the project Completed will ask you to confirm you did."
						type="warning"
						showIcon
						icon={<WarningOutlined />}
					/>
				)}
				{importableBalloons.droppedPages > 0 && (
					<Alert
						message={`${importableBalloons.droppedPages} page(s) of the source have no matching page here`}
						description="Their balloons cannot be imported because this drawing has fewer pages. Add them by hand after importing."
						type="warning"
						showIcon
					/>
				)}
			</div>

			<div className="flex flex-1 w-full overflow-hidden">
				<div className="w-[160px] min-w-[160px] shrink-0 bg-white/90 p-2 border-gray-300 flex flex-col gap-2 overflow-y-auto overflow-x-hidden min-h-0">
					<BallooningPages
						pageNumber={pageNumber}
						setPageNumber={setPageNumber}
						ballooningPages={projectData?.ballooning_pages || []}
						hoveringSidebar={false}
					/>
				</div>

				<div className="flex-1 flex h-full overflow-hidden">
					<div className="flex w-full h-full">
						<div className="flex-1 flex flex-col justify-center items-center overflow-hidden p-2">
							<Typography.Title level={5} className="text-center mb-2">
								Current: {formatCurrentTitle()}
							</Typography.Title>
							<BallooningViewer
								editable={false}
								activeBalloonId={activeBalloonId}
								setActiveBalloonId={setActiveBalloonId}
								centerOnActive={false}
								setCenterOnActive={() => undefined}
								localBalloons={scaledBalloons}
								setLocalBalloons={() => undefined}
								balloonSize={50}
								pageID={pageID ?? ''}
								pageNumber={pageNumber}
								setPageNumber={setPageNumber}
								showLeftPageBar={false}
								isTabletView={false}
							/>
						</div>

						<Divider type="vertical" style={{ height: '100%', margin: 0 }} />

						<div className="flex-1 flex flex-col justify-center items-center overflow-hidden p-2">
							<div className="flex items-center justify-center w-full mb-2 gap-2 flex-wrap">
								<Typography.Title level={5} className="mb-0 mr-2">
									Source
								</Typography.Title>
								<Select
									style={{ minWidth: 380, maxWidth: 520 }}
									showSearch
									allowClear={false}
									placeholder={
										currentPartName
											? `Type part or order name, e.g. ${truncate(
													currentPartName,
													24
											  )}`
											: 'Type a part or order name to search'
									}
									value={selectedProjectId ?? undefined}
									onChange={(value) => setSelectedProjectId(value ?? null)}
									// Options come from the server, so no client-side filtering.
									filterOption={false}
									onSearch={setSearchTerm}
									loading={projectLoading || searchLoading || sourceLoading}
									notFoundContent={
										searchLoading
											? 'Searching…'
											: searchTerm.trim().length >= SEARCH_MIN_LENGTH
											? 'No ballooned projects match that name'
											: `Type at least ${SEARCH_MIN_LENGTH} characters to search`
									}
									optionLabelProp="label"
								>
									{hasPreviousProjects && (
										<OptGroup
											label={`Matched to this part (${previousProjectsSorted.length})`}
										>
											{previousProjectsSorted.map((proj) => {
												const label = formatSourceLabel(proj);
												const isMostRecent = proj.id === mostRecentProjectId;
												const dateLabel = proj.created_time
													? new Date(proj.created_time).toLocaleDateString()
													: null;
												const fullLabel = dateLabel
													? `${label} · ${dateLabel}`
													: label;
												return (
													<Option
														key={proj.id}
														value={proj.id}
														label={fullLabel}
													>
														<div className="flex items-center justify-between gap-2">
															<div className="flex flex-col min-w-0">
																<span className="truncate font-medium">
																	{label}
																</span>
																{dateLabel && (
																	<Typography.Text
																		type="secondary"
																		className="text-xs"
																	>
																		Ballooned {dateLabel}
																	</Typography.Text>
																)}
															</div>
															{isMostRecent && (
																<Tag
																	color="blue"
																	icon={<ClockCircleOutlined />}
																	style={{ marginRight: 0 }}
																>
																	Most recent
																</Tag>
															)}
														</div>
													</Option>
												);
											})}
										</OptGroup>
									)}
									{searchOnlyResults.length > 0 && (
										<OptGroup label="Search results (different part)">
											{searchOnlyResults.map((proj) => {
												const label = formatSourceLabel(proj);
												const dateLabel = proj.created_time
													? new Date(proj.created_time).toLocaleDateString()
													: null;
												return (
													<Option
														key={proj.id}
														value={proj.id}
														label={`${label}${
															dateLabel ? ` · ${dateLabel}` : ''
														}`}
													>
														<div className="flex items-center justify-between gap-2">
															<div className="flex flex-col min-w-0">
																<span className="truncate font-medium">
																	{label}
																</span>
																<Typography.Text
																	type="secondary"
																	className="text-xs"
																>
																	{proj.balloon_count} balloon(s)
																	{dateLabel ? ` · ${dateLabel}` : ''}
																</Typography.Text>
															</div>
															<Tag color="orange" style={{ marginRight: 0 }}>
																Not matched
															</Tag>
														</div>
													</Option>
												);
											})}
										</OptGroup>
									)}
								</Select>
								{selectedProjectId &&
									selectedProjectId === mostRecentProjectId && (
										<Tag
											color="blue"
											icon={<ClockCircleOutlined />}
											style={{ marginRight: 0 }}
										>
											Most recent
										</Tag>
									)}
								{sourceProject?.created_time && (
									<Tooltip
										title={new Date(
											sourceProject.created_time
										).toLocaleString()}
									>
										<Typography.Text type="secondary" className="text-xs">
											Ballooned{' '}
											{new Date(
												sourceProject.created_time
											).toLocaleDateString()}
										</Typography.Text>
									</Tooltip>
								)}
							</div>
							{selectedProjectId ? (
								<BallooningViewer
									editable={false}
									activeBalloonId={activeBalloonId}
									setActiveBalloonId={setActiveBalloonId}
									centerOnActive={false}
									setCenterOnActive={() => undefined}
									localBalloons={sourcePageBalloons}
									setLocalBalloons={() => undefined}
									balloonSize={50}
									pageID={pageID ?? ''}
									pageNumber={pageNumber}
									setPageNumber={setPageNumber}
									showLeftPageBar={false}
									isTabletView={false}
									projectId={selectedProjectId}
								/>
							) : (
								<Empty
									description="Search for a part or order to preview its ballooning"
									className="my-8"
								/>
							)}
						</div>
					</div>
				</div>
			</div>

			<div className="flex justify-end items-center p-4 border-t border-gray-200 bg-white gap-3">
				{importableBalloons.balloons.length > 0 && (
					<Typography.Text type="secondary">
						Imports {importableBalloons.balloons.length} balloon(s)
					</Typography.Text>
				)}
				<Space>
					<Button onClick={handleCancel}>Cancel</Button>
					<Button
						type="primary"
						danger={isUnverifiedSelection}
						onClick={handleApprove}
						disabled={
							!selectedProjectId ||
							sourceLoading ||
							importableBalloons.balloons.length === 0
						}
					>
						{isUnverifiedSelection ? 'Import for review' : 'Approve'}
					</Button>
				</Space>
			</div>
		</div>
	);
};

export default BallooningReusabilityChecker;
